import { Drawer } from "@caprikorn/dynamo-ui";
import { useSteps } from "@caprikorn/dynamo-utils";
import { type FC, useId } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { ArrowLeftMd } from "@icons";
import type { UrlParam } from "@router/router.types";
import type { GetPod } from "@services/apiEcms/apiEcms.types";
import { useGetPod } from "@services/apiEcms/useGetPod";
import { usePostTechnicalRequest } from "@services/apiEcms/usePostTechnicalRequest";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./AddPodDrawer.module.css";
import type { AddPodDrawerProps } from "./AddPodDrawer.types";
import { AddPodForm } from "../form/AddPodForm/AddPodForm";
import type { AddPodFormData } from "../form/AddPodForm/AddPodForm.types";
import { mapAddPodFormToTechRequest } from "../form/AddPodForm/AddPodForm.utils";
import { PodSerialCard } from "../form/PodSerialCard/PodSerialCard";
import { SelectEnergyForm } from "../form/SelectEnergyForm/SelectEnergyForm";

export const AddPodDrawer: FC<AddPodDrawerProps> = ({ isShown, onHide }) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const { contractId = "", customerId = "" } = useParams<UrlParam>();
  const formId = useId();

  const {
    formState: { errors },
    setValue,
    control,
    handleSubmit,
  } = useForm<AddPodFormData>({
    defaultValues: {
      additionalAddress: "",
      attachments: [],
      civility: "",
      contract: contractId,
      client: customerId,
      requestedDate: "",
      email: "",
      energyType: "",
      firstName: "",
      lastName: "",
      options: "",
      siteAddress: "",
      siteCity: "",
      siteZipCode: "",
      phone: "",
      podDistributionSystemOperator: "",
      podSerial: "",
      podType: "",
      potency: "",
      requestedBenefit: "",
    },
    mode: FORM_VALIDATION_MODE,
  });
  const steps = ["energyChoice", "podSerial", "podForm"];

  const energyType = useWatch({ control, name: "energyType" });
  const attachments = useWatch({ control, name: "attachments" });
  const podSerial = useWatch({ control, name: "podSerial" });

  const { step, setStep, nextStep, previousStep, isFirst } = useSteps(
    steps,
    steps[0],
  );

  const { isPending, mutate } = usePostTechnicalRequest();

  const {
    data: podInfoData,
    isPending: isPodInfoPending,
    isError: isPodInfoError,
    mutate: mutateSerial,
  } = useGetPod({
    energy: energyType === "" ? null : energyType,
    podSerial,
  });

  const getPodType = (data: GetPod) => {
    if (data.isCommunicating) {
      return energyType === "ELEC" ? "Linky" : "Gazpar";
    }

    return "Non communicant";
  };

  const sendAddPodRequest = (data: AddPodFormData) => {
    if (step === "podSerial") {
      mutateSerial(
        {
          energy: energyType === "" ? null : energyType,
          podSerial,
        },
        {
          onSuccess: (podData) => {
            setValue("additionalAddress", podData.address.additionalAddress);
            setValue("siteAddress", podData.address.streetNumberAndName);
            setValue("siteZipCode", podData.address.zipCode);
            setValue("siteCity", podData.address.city);
            setValue("podType", getPodType(podData));
            setValue(
              "podDistributionSystemOperator",
              energyType === "ELEC" ? "ENEDIS" : "GRDF",
            );
            nextStep();
          },
        },
      );

      return;
    }

    if (step === "podForm") {
      mutate(mapAddPodFormToTechRequest(data), {
        onSuccess: onHide,
      });
    }
  };

  if (!isShown) return null;

  return (
    <Drawer onClose={onHide} size="lg">
      <Drawer.Header title={t("addPodDrawer.title")} />
      <Drawer.Body>
        <form id={formId} onSubmit={handleSubmit(sendAddPodRequest)}>
          {step === "energyChoice" && (
            <SelectEnergyForm
              energyType={energyType === "" ? null : energyType}
              setEnergyType={(value) => setValue("energyType", value)}
              setStep={setStep}
            />
          )}
          {step === "podSerial" && (
            <PodSerialCard
              control={control}
              isError={isPodInfoError}
              isPending={isPodInfoPending}
            />
          )}
          {step === "podForm" && (
            <AddPodForm
              control={control}
              data={podInfoData}
              errors={errors}
              setValue={setValue}
            />
          )}
        </form>
      </Drawer.Body>
      <Drawer.Footer className={classes.footer}>
        {!isFirst && (
          <>
            <Drawer.Footer.SideButton
              data-testid="AddPodDrawer-open"
              onClick={() => {
                previousStep();
                if (step === "podSerial") {
                  setValue("energyType", "");
                }
              }}
              startIcon={ArrowLeftMd}
            >
              {t("button.previous", { ns: "common" })}
            </Drawer.Footer.SideButton>
            {step === "podSerial" && isPodInfoError && (
              <Drawer.Footer.CancelButton
                disabled={isPodInfoError && attachments?.length === 0}
                onClick={nextStep}
              >
                {t("addPodDrawer.footer.addPodManually")}
              </Drawer.Footer.CancelButton>
            )}
            {step === "podSerial" ? (
              <Drawer.Footer.ConfirmButton
                disabled={Boolean(errors.podSerial)}
                form={formId}
                isLoading={isPodInfoPending}
                type="submit"
              >
                {t("addPodDrawer.footer.searchPod")}
              </Drawer.Footer.ConfirmButton>
            ) : (
              <Drawer.Footer.ConfirmButton
                form={formId}
                isLoading={isPending}
                type="submit"
              >
                {t("addPodDrawer.footer.addPod")}
              </Drawer.Footer.ConfirmButton>
            )}
          </>
        )}
      </Drawer.Footer>
    </Drawer>
  );
};
